import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "./_shared";
import { ExternalLink, Eye, EyeOff } from "lucide-react";

export default function ProfileTab({ client, onSave, canEdit, user }) {
  const [f, setF] = useState({ ...client });
  const [showAadhaar, setShowAadhaar] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Aadhaar is masked for roles that aren't Admin or the owning Advisor —
  // the backend already redacts the value, we just drive the show/hide affordance
  // off the server-provided `aadhaar_masked` flag so the toggle is only
  // meaningful when the full value is actually in hand.
  const aadhaarIsMasked = !!client.aadhaar_masked;
  const canEditAadhaar =
    user?.role === "Admin" ||
    (user?.role === "Advisor" && client.assigned_advisor_id === user?.id);
  const protonUrl = (f.documents_drive_folder_link || "").trim();

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Full name" v={f.full_name} on={(v) => set("full_name", v)} disabled={!canEdit} testId="profile-full-name" />
        <Field label="Email" v={f.email} on={(v) => set("email", v)} disabled={!canEdit} testId="profile-email" />
        <Field label="Mobile" v={f.mobile} on={(v) => set("mobile", v)} disabled={!canEdit} testId="profile-mobile" />
        <Field label="Occupation" v={f.occupation} on={(v) => set("occupation", v)} disabled={!canEdit} testId="profile-occupation" />
        <Field label="Date of birth" type="date" v={f.dob?.slice(0,10)} on={(v) => set("dob", v)} disabled={!canEdit} testId="profile-dob" />
        <Field label="PAN" v={f.pan} on={(v) => set("pan", v)} disabled={!canEdit} testId="profile-pan" />
        <div data-testid="profile-aadhaar-wrap">
          <label className="mb-1 flex items-center justify-between text-xs font-semibold text-muted-foreground">
            <span>Aadhaar Number {aadhaarIsMasked && <span className="ml-1 text-[10px] italic">(masked)</span>}</span>
            {aadhaarIsMasked && (
              <span className="text-[10px] text-muted-foreground">Visible only to Admin & owning Advisor</span>
            )}
          </label>
          <div className="flex gap-1">
            <input
              value={aadhaarIsMasked || !showAadhaar
                ? (aadhaarIsMasked ? (f.aadhaar_number || "") : maskForDisplay(f.aadhaar_number))
                : (f.aadhaar_number || "")}
              onChange={(e) => set("aadhaar_number", e.target.value)}
              disabled={!canEdit || !canEditAadhaar || aadhaarIsMasked}
              placeholder={aadhaarIsMasked ? "" : "1234 5678 9012"}
              inputMode="numeric"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-70"
              data-testid="profile-aadhaar"
            />
            {!aadhaarIsMasked && f.aadhaar_number && (
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => setShowAadhaar((s) => !s)}
                      className="h-10 w-10 p-0"
                      aria-label={showAadhaar ? "Hide" : "Show"}
                      data-testid="profile-aadhaar-toggle">
                {showAadhaar ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            )}
          </div>
        </div>
        <Field label="Marital status" v={f.marital_status} on={(v) => set("marital_status", v)} disabled={!canEdit} testId="profile-marital" />
        <Field label="Address" v={f.address} on={(v) => set("address", v)} disabled={!canEdit} testId="profile-address" />
        <Field label="Scheduled retirement" type="date" v={f.scheduled_retirement_date?.slice(0,10)} on={(v) => set("scheduled_retirement_date", v)} disabled={!canEdit} testId="profile-sched-retire" />
        <Field label="Desired retirement" type="date" v={f.desired_retirement_date?.slice(0,10)} on={(v) => set("desired_retirement_date", v)} disabled={!canEdit} testId="profile-desired-retire" />
      </div>

      <div className="rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="profile-proton-folder">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <label className="text-xs font-semibold text-primary">Documents · Proton Drive folder</label>
            <p className="mb-2 text-[11px] text-muted-foreground">
              A single Proton Drive URL that holds every document for this client. Distinct from the per-policy
              Proton links on the Insurance tab.
            </p>
            <input
              value={f.documents_drive_folder_link || ""}
              onChange={(e) => set("documents_drive_folder_link", e.target.value)}
              disabled={!canEdit}
              placeholder="https://drive.proton.me/urls/…"
              className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
              data-testid="profile-proton-input"
            />
          </div>
          {protonUrl && (
            <a href={protonUrl} target="_blank" rel="noreferrer"
               className="mt-6 inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary hover:underline"
               data-testid="profile-proton-open">
              <ExternalLink className="h-3.5 w-3.5" /> Open folder
            </a>
          )}
        </div>
      </div>

      {canEdit && (
        <Button onClick={() => onSave(f)} className="gap-2" data-testid="save-profile-btn">
          Save profile
        </Button>
      )}
    </div>
  );
}

function maskForDisplay(value) {
  if (!value) return "";
  const digits = String(value).replace(/\s/g, "");
  if (digits.length < 4) return digits;
  return `XXXX XXXX ${digits.slice(-4)}`;
}
